import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import bulmaCollapsible from "@creativebulma/bulma-collapsible";
import { useTranslation, Trans } from "react-i18next";
import { mdiVote, mdiAccountMultiple } from "@mdi/js";
import Icon from "@mdi/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { MessageContext } from "../../../../common/message/index";
import { GET } from "../../../../utils/fetchUtil";
import { isDesktop } from "../../../../utils/screenUtil";
import { getAccessToken } from "../../../../common/authorization/AuthorizeService";
import VotingFooterButtons from "../VotingFooterButtons";
import { Row, Column } from "../../../../common/columns";
import { cleanJoin } from "../../../../utils/stringUtil";
import ElectionDetail from "./ElectionDetail";
import Tooltip from "../../../../common/tooltip/Tooltip";
import Box from "../../../../common/box/Box";
import "./VotingPage.scss";
import ElectionTitle from "../customModals/ElectionTitle";

function VotingPage() {
  const { t } = useTranslation();
  const message = useContext(MessageContext);
  const { id } = useParams();
  const [electionDetail, setElectionDetail] = useState(true);
  const [panelColor, setPanelColor] = useState("purple-dark");
  const [election, setElection] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState([]);
  const [multipleChoice, setMultipleChoice] = useState(election?.electionType?.multipleChoice);

  const STATUS = {
    uPlaniranju: 1,
    zakljucano: 2,
    uTijeku: 3,
    zavrseno: 4,
    otkazano: 5,
  };

  useEffect(() => {
    GET(
      "/election/" + id,
      (response) => {
        setElection(response);
      },
      (error) => {
        message.toggle({
          title: t("requests.overview.supplement.infoBar.errorTitle"),
          content: error,
          hideNo: true,
          yesLabel: t("modalMessage.okLabel"),
          onYes: () => {
            message.toggle({});
          },
        });
      }
    );

    GET(
      "/candidates?electionId=" + id,
      (response) => {
        setCandidates(response);
      },
      (error) => {
        message.toggle({
          title: t("requests.overview.supplement.infoBar.errorTitle"),
          content: error,
          hideNo: true,
          yesLabel: t("modalMessage.okLabel"),
          onYes: () => {
            message.toggle({});
          },
        });
      }
    );
  }, [id]);

  useEffect(() => {
    if (election?.generatedStatus?.id === STATUS.uTijeku) {
      setPanelColor("vote-green");
    } else if (election?.generatedStatus?.id === STATUS.zavrseno) {
      setPanelColor("vote-red");
    } else {
      setPanelColor("purple-dark");
    }
    setMultipleChoice(election?.electionType?.multipleChoice);
  }, [election]);

  useEffect(() => {
    bulmaCollapsible.attach(".is-collapsible.candidates");
  }, [election]);

  const electionDetailToggle = () => {
    electionDetail ? setElectionDetail(false) : setElectionDetail(true);
  };

  const handleClickCandidate = (index) => {
    if (multipleChoice) {
      let newCandidateArray = [...selectedCandidate];
      if (newCandidateArray.length === 0) {
        newCandidateArray.push(candidates[index]);
      }

      if (selectedCandidate.length > 0) {
        selectedCandidate.find((candidate) => candidate.id === candidates[index].id) === undefined
          ? newCandidateArray.push(candidates[index])
          : (newCandidateArray = selectedCandidate.filter(
              (candidate) => candidate.id !== candidates[index].id
            ));
        setSelectedCandidate(newCandidateArray);
      } else {
        setSelectedCandidate(newCandidateArray);
      }
    } else {
      setSelectedCandidate([candidates[index]]);
    }
  };

  const fetchDoc = async (fileUrl) => {
    fetch(`${process.env.REACT_APP_GATEWAY_URL}${fileUrl}`, {
      method: "GET",
      headers: {
        "Request-Origin": "portal",
        Authorization: "Bearer " + (await getAccessToken()),
      },
      mode: "cors",
      credentials: "include",
    })
      .then((response) => response.blob())
      .then((file) => window.open(URL.createObjectURL(file)));
  };

  const tooltipContent = () => {
    if (election?.generatedStatus?.id === STATUS.zavrseno)
      return t("voting.buttonTooltip.electionEnded");
    else if (election?.generatedStatus?.id === STATUS.uTijeku && election?.errorMessage === null)
      return t("voting.buttonTooltip.choiceNumber", { count: election?.numberOfChoices });
    else if (
      election?.generatedStatus?.id === STATUS.zakljucano ||
      election?.generatedStatus?.id === STATUS.uPlaniranju
    )
      return t("voting.buttonTooltip.electionLocked");
    else return t("voting.buttonTooltip.voted");
  };

  return (
    <div>
      <ElectionTitle />
      <Row className="is-mobile mt-6">
        <Column size={12} className="has-text-centered">
          <div className="is-size-4">
            {election?.electionType?.typeName} -{" "}
            <span className={`has-text-${panelColor}`}>{election?.generatedStatus?.status}</span>
          </div>
          <span className="has-text-vote-green">{election?.errorMessage}</span>
        </Column>
      </Row>
      <Row isGapless={true} className="is-full is-align-items-center">
        <Column size={1}></Column>
        <Column
          size={11}
          className="is-flex is-align-items-center is-size-3 has-text-weight-medium has-pointer-cursor"
        >
          <Trans i18nKey={"voting.elections"}>
            <Icon
              path={mdiVote}
              size={2}
              className={`has-text-purple-dark mr-6`}
              onClick={electionDetailToggle}
            />
          </Trans>
        </Column>
      </Row>

      <Row isGapless={true}>
        <Column size={1} className=""></Column>
        <Column size={10}>
          <ElectionDetail electionDetail={electionDetail} election={election} />
        </Column>
        <Column size={1}></Column>
      </Row>
      <Row isGapless={true} className="is-full">
        <Column size={1}></Column>
        <Column
          size={11}
          className="is-flex is-align-items-center is-size-3 has-text-weight-medium"
        >
          <Trans i18nKey={"voting.candidates"} count={candidates.length}>
            <Icon path={mdiAccountMultiple} size={2} className={`has-text-purple-dark mr-6`} />
          </Trans>
        </Column>
      </Row>

      <div className="columns" id="candidatesL">
        <div className="column is-1"></div>
        <div className="column is-10">
          {isDesktop() && (
            <Box
              isBorderless={true}
              isRadiusless={true}
              isShadowless={true}
              statusIndicatorColor="purple-dark"
              statusIndicatorPosition="bottom"
              statusIndicatorWidth={1}
            >
              <Row isGapless={true} className="is-full has-text-weight-medium has-text-purple-dark">
                <Column size={1}>{t("voting.candidatesContent.ehkmsRegistrationNumber")}</Column>
                <Column size={3}>{t("voting.candidatesContent.nameAndSurname")}</Column>
                <Column size={7}>{t("voting.candidatesContent.institution")}</Column>
                <Column size={1}>{t("voting.candidatesContent.documentation")}</Column>
              </Row>
            </Box>
          )}

          {candidates.map((candidate, index) => {
            return (
              <Box
                isBorderless={false}
                isRadiusless={false}
                isShadowless={false}
                statusIndicatorColor={
                  selectedCandidate.find((cand) => cand.id === candidate.id) && "vote-green"
                }
                statusIndicatorPosition="bottom"
                key={index}
              >
                {isDesktop() ? (
                  <Row isGapless={true} className="is-full">
                    <Column
                      size={1}
                      onClick={
                        election?.generatedStatus?.id === STATUS.uTijeku &&
                        election?.errorMessage === null
                          ? () => handleClickCandidate(index)
                          : () => {}
                      }
                      className={"has-pointer-cursor"}
                    >
                      {candidate?.evidencijskiBrojEhkms}
                    </Column>
                    <Column
                      size={3}
                      onClick={
                        election?.generatedStatus?.id === STATUS.uTijeku &&
                        election?.errorMessage === null
                          ? () => handleClickCandidate(index)
                          : () => {}
                      }
                      className={"has-pointer-cursor"}
                    >
                      {candidate?.nameAndSurname}
                    </Column>
                    <Column
                      size={7}
                      onClick={
                        election?.generatedStatus?.id === STATUS.uTijeku &&
                        election?.errorMessage === null
                          ? () => handleClickCandidate(index)
                          : () => {}
                      }
                      className={"has-pointer-cursor"}
                    >
                      {candidate?.institution}
                    </Column>
                    <Column
                      size={1}
                      onClick={
                        candidate?.documentation.length > 0
                          ? () => {}
                          : election?.generatedStatus?.id === STATUS.uTijeku &&
                            election?.errorMessage === null
                          ? () => handleClickCandidate(index)
                          : () => {}
                      }
                      className={candidate?.documentation.length === 0 ? "has-pointer-cursor" : ""}
                    >
                      {candidate?.documentation.map((document, index) => {
                        let docName = document.fileUrl.split("_user_");
                        return (
                          <Tooltip delay={0} content={docName[1]} key={index}>
                            <FontAwesomeIcon
                              icon={faFile}
                              className="is-size-4 ml-1 has-pointer-cursor"
                              key={index}
                              onClick={() => fetchDoc(document?.fileUrl)}
                            />
                          </Tooltip>
                        );
                      })}
                    </Column>
                  </Row>
                ) : (
                  <Row className={"is-multiline"}>
                    <a href={`#candidateList${index}`} data-action="collapse">
                      <Column size={12}>
                        <span className="has-text-purple-dark has-text-weight-medium is-size-7">{`${t(
                          "voting.candidatesContent.nameAndSurname"
                        )}: `}</span>
                        <br />
                        <span>{`${candidate?.nameAndSurname}`}</span>
                      </Column>
                    </a>
                    <div
                      id={`candidateList${index}`}
                      className="is-collapsible candidates"
                      data-parent="candidatesL"
                    >
                      <Column size={12}>
                        <span className="has-text-purple-dark has-text-weight-medium is-size-7">
                          {`${t("voting.candidatesContent.ehkmsRegistrationNumber")}: `}
                        </span>
                        <br />
                        <span className="has-text-bleck">{`${candidate?.evidencijskiBrojEhkms}`}</span>
                        <br />
                        <span className="has-text-purple-dark has-text-weight-medium is-size-7">
                          {`${t("voting.candidatesContent.institution")}: `}
                        </span>
                        <br />
                        <span>{`${candidate?.institution}`}</span>
                        <br />
                        {candidate?.documentation.length > 0 && (
                          <>
                            <span className="has-text-purple-dark has-text-weight-medium is-size-7">
                              {`${t("voting.candidatesContent.documentation")}: `}
                            </span>
                            <br />
                          </>
                        )}
                        {candidate?.documentation.map((document, index) => {
                          let docName = document.fileUrl.split("_user_");
                          return (
                            <div
                              onClick={async () => {
                                fetch(`${process.env.REACT_APP_GATEWAY_URL}${document?.fileUrl}`, {
                                  method: "GET",
                                  headers: {
                                    "Request-Origin": "portal",
                                    Authorization: "Bearer " + (await getAccessToken()),
                                  },
                                  mode: "cors",
                                  credentials: "include",
                                })
                                  .then((response) => response.blob())
                                  .then((file) => window.open(URL.createObjectURL(file)));
                              }}
                              key={index}
                            >
                              {docName[1]}
                            </div>
                          );
                        })}
                        <button
                          className="button is-purple is-outlined has-text-purple-dark"
                          onClick={() => handleClickCandidate(index)}
                          disabled={
                            election?.generatedStatus?.id === STATUS.uTijeku &&
                            election?.errorMessage === null
                              ? false
                              : true
                          }
                        >
                          {!multipleChoice
                            ? t("voting.candidatesContent.select")
                            : selectedCandidate.find((cand) => cand.id === candidate.id)
                            ? t("voting.candidatesContent.cancel")
                            : t("voting.candidatesContent.select")}
                        </button>
                      </Column>
                    </div>
                  </Row>
                )}
              </Box>
            );
          })}
        </div>
      </div>
      <div className="mb-5">
        <VotingFooterButtons
          selectedCandidate={selectedCandidate}
          id={id}
          disabled={selectedCandidate.length === election?.numberOfChoices ? false : true}
          content={
            election?.generatedStatus?.id === STATUS.zavrseno
              ? t("voting.buttonTooltip.electionEnded")
              : election?.generatedStatus?.id === STATUS.uTijeku && election?.errorMessage === null
              ? t("voting.buttonTooltip.choiceNumber", { count: election?.numberOfChoices })
              : [STATUS.zakljucano, STATUS.uPlaniranju, STATUS.otkazano].includes(
                  election?.generatedStatus?.id
                )
              ? t("voting.buttonTooltip.electionLocked")
              : t("voting.buttonTooltip.voted")
          }
          msgToggle={
            election.errorMessage !== null
              ? true
              : election?.generatedStatus?.id !== STATUS.uTijeku
              ? true
              : false
          }
        />
      </div>
    </div>
  );
}

export default VotingPage;
